
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreditCard, Coins } from "lucide-react";
import { useProfileCredits } from "@/hooks/use-profile-credits";
import { useStableAvatar } from "@/hooks/use-stable-avatar";
import { Book } from "@/types/book";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { trackBeginCheckout } from "@/utils/analytics";
import { useCurrencyDetection } from "@/hooks/use-currency-detection";

interface IndividualBookPurchaseProps {
  book: Book;
  isOpen: boolean;
  onClose: () => void;
  onPurchaseComplete: () => void;
  individualPrice: number;
}

export function IndividualBookPurchase({ 
  book, 
  isOpen, 
  onClose, 
  onPurchaseComplete,
  individualPrice 
}: IndividualBookPurchaseProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const { creditBalance, canSpendCredits, spendCredits, isSpendingCredits } = useProfileCredits();
  const { currentProfile } = useStableAvatar();
  const { session } = useAuth();
  const { toast } = useToast();
  const { currency } = useCurrencyDetection();
  const creditsRequired = 4;

  const handleCreditPurchase = async () => {
    if (!session?.user?.id || !currentProfile?.id) return;
    
    try {
      setIsProcessing(true);
      
      // First spend the credits
      spendCredits({
        amount: creditsRequired,
        reason: 'individual_book_purchase',
        referenceId: crypto.randomUUID()
      });
      
      // Record the individual book purchase
      const { error } = await supabase
        .from('individual_book_purchases')
        .insert({
          user_id: session.user.id,
          profile_id: currentProfile.id,
          book_id: book.id,
          purchase_type: 'credits',
          credits_spent: creditsRequired,
          access_enabled: true
        });
      
      if (error) throw error;
      
      onPurchaseComplete();
      onClose();
      
    } catch (error) {
      console.error('Error purchasing book with credits:', error);
      toast({
        title: "Purchase Failed",
        description: "Unable to complete your purchase. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCashPurchase = async () => {
    if (!session?.user?.id) return;
    
    try {
      setIsProcessing(true);
      
      // Track begin checkout event
      trackBeginCheckout({
        value: individualPrice,
        currency: currency.code,
        items: [{
          item_id: `book_${book.id}`,
          item_name: book.title,
          item_category: 'Individual Book',
          price: individualPrice,
          quantity: 1
        }]
      });
      
      // Create Stripe checkout session for individual book
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          bookId: book.id,
          isIndividualPurchase: true,
          individualPrice: individualPrice
        }
      });
      
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Checkout Error",
        description: "Unable to start checkout. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md glass-morphism text-white backdrop-blur-sm">
        <DialogHeader>
          <DialogTitle className="text-xl font-light text-white">Purchase Book</DialogTitle>
          <DialogDescription className="text-gray-400">
            Choose how you'd like to purchase "{book.title}"
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="credits" className="w-full">
          <TabsList className="grid w-full grid-cols-2 glass-card">
            <TabsTrigger value="credits" className="flex items-center gap-2">
              <Coins className="w-4 h-4" />
              Credits
            </TabsTrigger>
            <TabsTrigger value="cash" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Cash
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="credits" className="space-y-4">
            <div className="text-center space-y-2">
              <div className="text-2xl font-bold text-yellow-400">{creditsRequired} Credits</div>
              <div className="text-sm text-gray-400">
                Your balance: {creditBalance} credits
              </div>
            </div>
            
            {!canSpendCredits && (
              <div className="text-sm text-amber-400 text-center p-3 bg-amber-400/10 rounded-lg">
                {creditBalance < creditsRequired 
                  ? "Not enough credits. Purchase a bundle to get more credits."
                  : "Credit spending requires approval from the account holder."}
              </div>
            )}
            
            <Button 
              onClick={handleCreditPurchase}
              disabled={!canSpendCredits || isProcessing || isSpendingCredits}
              className="w-full bg-yellow-600 hover:bg-yellow-700"
            >
              {isProcessing || isSpendingCredits ? "Processing..." : `Use ${creditsRequired} Credits`}
            </Button>
          </TabsContent>
          
          <TabsContent value="cash" className="space-y-4">
            <div className="text-center space-y-2">
              <div className="text-2xl font-bold text-green-400">${individualPrice}</div>
              <div className="text-sm text-gray-400">
                One-time purchase for lifetime access
              </div>
            </div>
            
            <Button 
              onClick={handleCashPurchase}
              disabled={isProcessing}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {isProcessing ? "Processing..." : `Pay $${individualPrice}`}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
